import dayjs from 'dayjs';
import dayjsTimezonePlugin from 'dayjs/plugin/timezone';
import dayjsUtcPlugin from 'dayjs/plugin/utc';
import { CubeTimeDimensionGranularity } from '../enum/cube-time-dimension-granularity.enum';
import { ShiftDirectionEnum } from '../enum/shift-direction.enum';
import { WeekdayIndexEnum, Weekdays } from '../enum/weekdays/weekdays.enum';
import { getGranularityNumericalOffset } from './granularity.util';
dayjs.extend(dayjsUtcPlugin);
dayjs.extend(dayjsTimezonePlugin);

const ONE_DAY_IN_MILLISECONDS = 86400000;

/**
 * shiftDateToNearestGranularity({date, granularity, direction}) returns a date shifted
 * based on granularity and direction.
 *
 * Note: For weekly granularity, weeks start on Monday.
 * This is for Cube support.
 *
 * @param {Date} props.date
 * @param {CubeTimeDimensionGranularity} props.granularity
 * @param {ShiftDirectionEnum} props.direction
 * @return {Date} date
 */
export const shiftDateToNearestGranularity = (props: {
  date: Date;
  granularity: CubeTimeDimensionGranularity;
  direction: ShiftDirectionEnum;
}): Date => {
  const { date, granularity, direction } = props;
  if (granularity === CubeTimeDimensionGranularity.Day) {
    return date;
  }
  const isWeekGranularity = granularity === CubeTimeDimensionGranularity.Week;
  const weekdayIndex = date.getUTCDay();
  const shiftLeft = direction === ShiftDirectionEnum.Left;
  // 1, 7 or last integer date of given month
  const initialShiftOffsetState = shiftLeft
    ? 1
    : isWeekGranularity
    ? 7
    : new Date(date.getUTCFullYear(), date.getUTCMonth() + 1, 0).getUTCDate();
  // 7 or initialShiftOffsetState - 1 due to sunday boundary edge case
  const shiftOffsetValue = isWeekGranularity
    ? weekdayIndex || (shiftLeft ? 7 : initialShiftOffsetState - 1)
    : date.getUTCDate();
  const shiftOffsetProduct =
    (initialShiftOffsetState - shiftOffsetValue) * ONE_DAY_IN_MILLISECONDS;
  return new Date(date.getTime() + shiftOffsetProduct);
};

export const getTimezoneUtcOffset = (timezone: string): number => {
  return dayjs.utc().tz(timezone).utcOffset();
};

export const correlateDatetimeByUtcOffset = (
  date: Date,
  offset: number
): Date => {
  const offsetInMs = minutesToMilliseconds(offset);
  return new Date(
    offsetInMs > 0
      ? date.getTime() + Math.abs(offsetInMs)
      : date.getTime() - Math.abs(offsetInMs)
  );
};

export const toTimezonedIsoDate = (
  isoUtc: string,
  timezone: string
): string => {
  // interpret the input as utc, convert to target tz, keep the offset in the string
  return dayjs.utc(isoUtc).tz(timezone).format();
};

export const getDateByDaysOffset = (
  date: Date,
  offset: number,
  forwardFacing = false
): Date => {
  const delta = forwardFacing
    ? date.getUTCDate() + offset
    : date.getUTCDate() - offset;
  return new Date(new Date(date).setUTCDate(delta));
};

export const getStartDate = (
  granularity: CubeTimeDimensionGranularity,
  startDate: string,
  endDate: string
): Date => {
  if (granularity === CubeTimeDimensionGranularity.Week) {
    return getFirstUtcDayInDateRange(
      new Date(startDate),
      new Date(endDate),
      WeekdayIndexEnum.Monday
    );
  } else if (granularity === CubeTimeDimensionGranularity.Month) {
    return new Date(
      new Date(startDate).getUTCFullYear(),
      new Date(startDate).getUTCMonth(),
      1
    );
  } else {
    return new Date(startDate);
  }
};

export const isSameDateInGranularity = (
  date1: Date,
  date2: Date,
  granularity: CubeTimeDimensionGranularity
): Boolean => {
  switch (granularity) {
    case CubeTimeDimensionGranularity.Day:
      return isSameDate(date1, date2);
    case CubeTimeDimensionGranularity.Week:
      return isSameWeek(date1, date2);
    case CubeTimeDimensionGranularity.Month:
      return isSameMonth(date1, date2);
  }
};

export const spreadDateRangeForMonthlyGranularity = (
  startDate: Date,
  endDate: Date,
  granularity: CubeTimeDimensionGranularity
): Date[] => {
  if (granularity !== CubeTimeDimensionGranularity.Month) {
    throw new Error('Granularity is not monthly');
  }
  const dates = [];
  let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  return dates;
};

export const spreadDateRange = (
  startDate: Date,
  endDate: Date,
  offset: number = 1
): Date[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates = [];
  for (
    let i: Date = start;
    i.getTime() <= end.getTime();
    i.setUTCDate(i.getUTCDate() + offset)
  ) {
    dates.push(new Date(i));
  }
  return dates;
};

export const spreadDateRangeForGranularity = (
  startDate: Date,
  endDate: Date,
  granularity: CubeTimeDimensionGranularity
): Date[] => {
  const isMonthGranularity = granularity === CubeTimeDimensionGranularity.Month;
  if (isMonthGranularity) {
    return spreadDateRangeForMonthlyGranularity(
      startDate,
      endDate,
      granularity
    );
  }
  return spreadDateRange(
    startDate,
    endDate,
    getGranularityNumericalOffset(granularity)
  );
};

export const spreadDateRangeForWeekday = (
  startDate: Date,
  endDate: Date,
  weekday: Weekdays
): Date[] => {
  const dates = [];
  const dayIndex = convertWeekdayToIndex(weekday);

  let current = new Date(startDate);
  while (current.getUTCDay() !== dayIndex) {
    current.setUTCDate(current.getUTCDate() + 1);
  }

  for (
    const i: Date = current;
    i.getTime() <= endDate.getTime();
    i.setUTCDate(i.getUTCDate() + 7)
  ) {
    dates.push(new Date(i));
  }

  return dates;
};

export const getDayOfWeek = (dayOfWeek: number) => {
  if (isNaN(dayOfWeek)) {
    return null;
  }
  return [
    Weekdays.Sunday,
    Weekdays.Monday,
    Weekdays.Tuesday,
    Weekdays.Wednesday,
    Weekdays.Thursday,
    Weekdays.Friday,
    Weekdays.Saturday,
  ][dayOfWeek];
};

export function isWeekday(granularity: any): granularity is Weekdays {
  return Object.values(Weekdays).includes(granularity);
}

export const getFirstUtcDayInDateRange = (
  startDate: Date,
  endDate: Date,
  utcDayIndex: number
): Date => {
  if (isNaN(utcDayIndex)) {
    throw new Error('utcDayIndex is NaN');
  }
  if (utcDayIndex < 0 || utcDayIndex > 6) {
    throw new Error('utcDayIndex is invalid');
  }
  for (
    let givenDate = new Date(startDate);
    givenDate <= new Date(endDate);
    givenDate.setUTCDate(givenDate.getUTCDate() + 1)
  ) {
    if (givenDate.getUTCDay() === utcDayIndex) {
      return givenDate;
    }
  }
  throw new Error(
    `getFirstUtcDayInDateRange could not be established with inputs ${JSON.stringify(
      { startDate, endDate, utcDayIndex }
    )}`
  );
};

export const convertWeekdayToWeekdayIndex = (
  weekday: Weekdays
): WeekdayIndexEnum => {
  return WeekdayIndexEnum[weekday.charAt(0).toUpperCase() + weekday.slice(1)];
};

export const convertWeekdayToIndex = (weekday: Weekdays): WeekdayIndexEnum => {
  switch (weekday) {
    case Weekdays.Sunday:
      return WeekdayIndexEnum.Sunday;
    case Weekdays.Monday:
      return WeekdayIndexEnum.Monday;
    case Weekdays.Tuesday:
      return WeekdayIndexEnum.Tuesday;
    case Weekdays.Wednesday:
      return WeekdayIndexEnum.Wednesday;
    case Weekdays.Thursday:
      return WeekdayIndexEnum.Thursday;
    case Weekdays.Friday:
      return WeekdayIndexEnum.Friday;
    case Weekdays.Saturday:
      return WeekdayIndexEnum.Saturday;
    default:
      throw new Error('Invalid weekday');
  }
};

export const isSameDate = (date1: Date, date2: Date): Boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
};

export const isSameWeek = (date1: Date, date2: Date): Boolean => {
  const startOfWeekDate1 = getStartOfWeek(date1);
  const startOfWeekDate2 = getStartOfWeek(date2);

  return (
    startOfWeekDate1.toISOString().split('T')[0] ===
    startOfWeekDate2.toISOString().split('T')[0]
  );
};

export const isSameMonth = (date1: Date, date2: Date): Boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth()
  );
};

export const getStartOfWeek = (date: Date): Date => {
  const newDate = new Date(date);
  const day = newDate.getUTCDay() || 7;
  newDate.setUTCDate(newDate.getUTCDate() - day + 1);
  return new Date(
    Date.UTC(
      newDate.getUTCFullYear(),
      newDate.getUTCMonth(),
      newDate.getUTCDate()
    )
  );
};

export const millisecondsToHours = (milliseconds: number): number => {
  return milliseconds / 1000 / 60 / 60;
};

export const minutesToMilliseconds = (minutes: number): number => {
  return minutes * 60000;
};

export const toNonZuluTimeString = (date: Date): string => {
  return date.toISOString().slice(0, -1);
};

export const getPeriodBoundariesFromStartDateAndGranularity = (
  startDate: Date,
  granularity: CubeTimeDimensionGranularity
) => {
  const start = new Date(startDate);
  if (granularity === CubeTimeDimensionGranularity.Month) {
    const thisMonthIdx = start.getUTCMonth();
    const endDate = getDateByDaysOffset(
        new Date(new Date(start).setUTCMonth(thisMonthIdx + 1)),
        1
      ),
      ppStartDate = new Date(new Date(start).setUTCMonth(thisMonthIdx - 1)),
      ppEndDate = getDateByDaysOffset(start, 1);
    return [start, endDate, ppStartDate, ppEndDate];
  }
  const offset = getGranularityNumericalOffset(granularity) - 1;
  const previousPeriodEndDate = getDateByDaysOffset(start, 1);
  const previousPeriodStartDate = getDateByDaysOffset(
    previousPeriodEndDate,
    offset
  );
  const endDate = getDateByDaysOffset(start, offset, true);
  return [start, endDate, previousPeriodStartDate, previousPeriodEndDate];
};

export const toDate = (obj: unknown): Date | null => {
  if (!obj) return null;
  if (obj instanceof Date && !isNaN(obj.getTime())) return obj;
  const d = new Date(obj as any);
  return isNaN(d.getTime()) ? null : d;
};

export const dateKeyUTC = (d: Date): string => d.toISOString().slice(0, 10);

export const isoWeekKeyUTC = (d: Date): string => {
  const date = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const isoYear = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  const week =
    1 + Math.round((date.getTime() - firstThursday.getTime()) / 604800000);
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
};

export const monthKeyUTC = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;

export const bucketKey = (d: Date, g: CubeTimeDimensionGranularity): string => {
  if (g === CubeTimeDimensionGranularity.Day) return dateKeyUTC(d);
  if (g === CubeTimeDimensionGranularity.Week) return isoWeekKeyUTC(d);
  return monthKeyUTC(d);
};

export const distinctPeriodCount = (
  startDate: Date,
  endDate: Date,
  granularity?: CubeTimeDimensionGranularity
): number => {
  const keys = new Set<string>();
  let current = new Date(startDate);

  while (current <= endDate) {
    let key: string;

    switch (granularity) {
      case CubeTimeDimensionGranularity.Week:
        key = isoWeekKeyUTC(current);
        // move to next week
        current.setUTCDate(current.getUTCDate() + 7);
        break;

      case CubeTimeDimensionGranularity.Month:
        key = monthKeyUTC(current);
        // move to first day of next month
        current.setUTCMonth(current.getUTCMonth() + 1, 1);
        break;

      case CubeTimeDimensionGranularity.Day:
      default:
        key = dateKeyUTC(current);
        // move to next day
        current.setUTCDate(current.getUTCDate() + 1);
        break;
    }

    keys.add(key);
  }

  return keys.size;
};
